import { classNames } from '@panwds/tailwindcss';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useCallback, useEffect, useRef } from 'react';
import { isNilOrEmptyString } from '../../utils/index.js';
import { LoadingIcon } from '../Icon/SemanticIcons.js';
import { List, ListItem } from '../List/index.js';
import { Body } from '../Typography/index.js';
import { SelectAllListItem } from './SelectAllListItem.js';
const itemSizes = {
    sm: 28,
    md: 32,
    lg: 40
};
const maxItemsShown = {
    5: 5,
    10: 10
};
export function SelectList(props) {
    const { emptyState = (React.createElement(Body, { addClassName: "twds-h-full twds-flex twds-items-center twds-m-2", appearance: "secondary", as: "div" }, "No matching options")), isLoading = false, isOpen, listItemStyle, menuStyle, dataMetrics, dataTestId, getMenuProps, onMenuKeyEnter, items, highlightedIndex, search = null, selectedItems, selectAllItem, enableMultiSelect, getItemProps, showSelectAll, selectedValues, formatOption, fitToItemWidth, itemsShown, listItemSize = 'md', draftSelectionsControl, footer } = props;
    const measureRef = useRef(null);
    const itemSize = useCallback((item) => {
        return formatOption?.height ? formatOption.height(item, itemSizes[listItemSize]) : itemSizes[listItemSize];
    }, [formatOption]);
    const estimateSize = useCallback((index) => {
        const item = items[index];
        return itemSize(item);
    }, [items, itemSize]);
    const rowVirtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => measureRef.current,
        estimateSize,
        overscan: fitToItemWidth ? items.length : 10
    });
    useEffect(() => {
        if (isOpen && highlightedIndex > -1)
            rowVirtualizer.scrollToIndex(highlightedIndex);
    }, [highlightedIndex, isOpen]);
    const totalHeight = !items?.length
        ? undefined
        : showSelectAll
            ? rowVirtualizer.getTotalSize() - 32
            : rowVirtualizer.getTotalSize();
    return (React.createElement(List, { dataMetrics: dataMetrics, dataTestId: dataTestId, isOpen: isOpen, style: {
            margin: '0.125rem 0',
            ...(!isNilOrEmptyString(itemsShown) && { maxHeight: '' }),
            ...menuStyle,
            ...(fitToItemWidth && { width: 'fit-content', minWidth: 'auto' })
        }, ...getMenuProps({
            onKeyDown: (event) => {
                if (onMenuKeyEnter && (event.key === 'Enter' || event.key === ' ')) {
                    onMenuKeyEnter(event, items[highlightedIndex]);
                }
                if (event.key === 'Escape') {
                    // Prevent the escape action from cascading, so that it only
                    // closes this select and not a Popover or Modal that it may
                    // be inside of.
                    event.preventDefault();
                }
            },
            onClick: (event) => {
                event.stopPropagation();
            }
        }) },
        React.createElement("div", { className: "twds-box-border twds-border-gray-200 twds-bg-white twds-border-b twds-rounded-t dark:twds-bg-black dark:twds-border-gray-700", style: {
                ...listItemStyle
            } },
            search,
            showSelectAll && (React.createElement(SelectAllListItem, { highlightedIndex: highlightedIndex, selectedItems: selectedItems, selectAllItem: selectAllItem, getItemProps: getItemProps }))),
        React.createElement("div", { className: classNames('twds-box-border twds-overflow-y-auto twds-scrollbar-small dark:twds-scrollbar-track-dark dark:twds-scrollbar-thumb-dark dark:twds-scrollbar-corner-dark twds-overflow-x-hidden twds-bg-white dark:twds-bg-black', draftSelectionsControl || footer
                ? 'twds-border-0 twds-border-b twds-border-gray-200 dark:twds-border-gray-950 twds-border-solid'
                : 'twds-shadow-md'), ref: measureRef, style: {
                maxHeight: !isNilOrEmptyString(itemsShown)
                    ? `${itemSizes[listItemSize ?? 'md'] * (maxItemsShown[itemsShown] ?? 10)}px`
                    : '100%',
                ...(fitToItemWidth ? { width: 'fit-content', minWidth: '100%' } : { minWidth: '100%' })
            } },
            React.createElement("div", { className: "twds-box-border twds-relative ", style: {
                    height: totalHeight,
                    ...(fitToItemWidth ? { width: 'fit-content', minWidth: '100%' } : { minWidth: '100%' })
                } }, isLoading ? (React.createElement("div", { className: "twds-box-border twds-flex twds-items-center twds-justify-center twds-bg-white twds-p-4 dark:twds-bg-black" },
                React.createElement(LoadingIcon, null))) : rowVirtualizer.getVirtualItems().length === 0 ? (emptyState) : (rowVirtualizer.getVirtualItems().map((virtualRow) => {
                if (virtualRow.index === 0 && items[virtualRow.index] === selectAllItem)
                    return null;
                const item = items[virtualRow.index];
                const start = showSelectAll ? virtualRow.start - virtualRow.size : virtualRow.start;
                const highlighted = highlightedIndex === virtualRow.index;
                return (React.createElement(ListItem, { dataTestId: virtualRow.index, dataMetrics: virtualRow.index, active: highlighted, checked: enableMultiSelect && selectedValues?.includes(item.value), hasCheckbox: enableMultiSelect, ...getItemProps({
                        disabled: item.disabled,
                        item,
                        index: virtualRow.index
                    }), ...item, key: item.key ?? item.value, style: {
                        ...(fitToItemWidth
                            ? { minWidth: 'fit-content' }
                            : {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${start}px)`
                            }),
                        height: itemSize(item),
                        ...listItemStyle
                    }, addClassName: classNames('twds-font-normal', listItemSize === 'lg' ? 'twds-text-sm' : 'twds-text-xs') }, formatOption?.view ? formatOption.view(item) : item.children || item.value));
            })))),
        draftSelectionsControl ?? footer));
}
//# sourceMappingURL=SelectList.js.map